import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Shield, Users, DollarSign, AlertTriangle, BarChart3,
  LogOut, RefreshCw, Eye, UserCog, Cpu, Activity, Wifi, Wallet, Zap, MessageSquare, Star,
  CheckSquare, TrendingUp, Server
} from 'lucide-react';

// Import admin components
import AuditLog from './AuditLog';
import OnlineUsersBox from './OnlineUsersBox';
import RevenueAnalytics from './RevenueAnalytics';
import SystemHealth from './SystemHealth';
import UserAnalytics from './UserAnalytics';
import CostDashboard from './CostDashboard';
import ErrorLog from './ErrorLog';
import LiveActivityFeed from './LiveActivityFeed';
import FeedbackManagement from './FeedbackManagement';
import ExportFeedbackAdmin from './ExportFeedbackAdmin';
import useAdminWebSocket from '../../hooks/useAdminWebSocket';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin Login Component
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/admin/auth/login`, {
        email,
        password
      });

      const { access_token } = response.data;
      localStorage.setItem('admin_token', access_token);
      toast.success('Admin login successful');
      onLogin(access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-zinc-800/50 border-zinc-700 backdrop-blur">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Admin Portal</h1>
          <p className="text-zinc-400 mt-2">Enter your admin credentials</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-zinc-300">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500"
              required
              data-testid="admin-email-input"
            />
          </div>

          <div>
            <Label className="text-zinc-300">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500"
              required
              data-testid="admin-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            disabled={loading}
            data-testid="admin-login-btn"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Shield className="h-4 w-4 mr-2" />
            )}
            Sign In
          </Button>
        </form>
      </Card>
    </div>
  );
}

// User Management Component
function UserManagement({ adminToken }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [impersonating, setImpersonating] = useState(null);
  const [migratingAll, setMigratingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [bulkPlan, setBulkPlan] = useState('free');
  const [bulkApplying, setBulkApplying] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${adminToken}` }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (planFilter) params.plan_filter = planFilter;
      const response = await axios.get(`${API}/api/admin/users`, { ...axiosConfig, params });
      setUsers(response.data.users || []);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilter]);

  const handleImpersonate = async (userId, userEmail) => {
    const reason = prompt('Enter reason for impersonation (required for audit):');
    if (!reason) return;

    try {
      const response = await axios.post(
        `${API}/api/admin/impersonate/start`,
        { user_id: userId, reason },
        axiosConfig
      );

      toast.success(`Now impersonating ${userEmail}`);
      setImpersonating(response.data);
      
      // Open main app in new tab with impersonation token
      const impersonationUrl = `${window.location.origin}/?impersonate_token=${response.data.token}`;
      window.open(impersonationUrl, '_blank');
    } catch (error) {
      toast.error('Failed to start impersonation');
    }
  };

  const handleUpdatePlan = async (userId, newPlan) => {
    if (!window.confirm(`Change plan to "${newPlan}"? This takes effect immediately.`)) return;
    try {
      await axios.put(
        `${API}/api/admin/users/${userId}/plan`,
        { plan: newPlan },
        axiosConfig
      );
      toast.success('Plan updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const handleAddCredits = async (userId) => {
    const credits = prompt('Enter credits to add (positive number):');
    if (!credits || isNaN(credits)) return;
    const parsed = parseInt(credits);
    if (parsed <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    try {
      await axios.post(
        `${API}/api/admin/users/${userId}/credits`,
        { amount: parsed },
        axiosConfig
      );
      toast.success(`Added ${credits} credits`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to add credits');
    }
  };

  const handleAddWalletBalance = async (userId) => {
    const amount = prompt('Enter wallet amount in ₹ (INR) to add (positive number):');
    if (!amount || isNaN(amount)) return;
    if (parseFloat(amount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }

    try {
      await axios.post(
        `${API}/api/admin/users/${userId}/wallet`,
        { amount: parseFloat(amount) },
        axiosConfig
      );
      toast.success(`Added ₹${amount} to wallet`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to add wallet balance');
    }
  };

  const handleMigrateUser = async (userId) => {
    try {
      const response = await axios.post(
        `${API}/api/admin/users/${userId}/migrate`,
        {},
        axiosConfig
      );
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to migrate user');
    }
  };

  const handleMigrateAllUsers = async () => {
    if (!window.confirm('This will migrate ALL users to the new pricing system. Continue?')) return;
    
    setMigratingAll(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/users/migrate-all`,
        {},
        axiosConfig
      );
      toast.success(`Migrated ${response.data.migrated_count} users`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to migrate users');
    } finally {
      setMigratingAll(false);
    }
  };

  const handleBulkApply = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Change plan to "${bulkPlan}" for ${selectedIds.length} user(s)?`)) return;
    setBulkApplying(true);
    try {
      const response = await axios.post(
        `${API}/api/admin/users/bulk-action`,
        { user_ids: selectedIds, action: 'change_plan', value: bulkPlan },
        axiosConfig
      );
      toast.success(response.data.message);
      setSelectedIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkApplying(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(search.toLowerCase()) ||
    user.name?.toLowerCase().includes(search.toLowerCase())
  );

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every(u => selectedIds.includes(u.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredUsers.find(u => u.id === id)));
    } else {
      const newIds = filteredUsers.map(u => u.id).filter(id => !selectedIds.includes(id));
      setSelectedIds(prev => [...prev, ...newIds]);
    }
  };

  const toggleSelectUser = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Plan badge colors
  const getPlanBadgeClass = (plan) => {
    switch(plan) {
      case 'business': return 'bg-purple-100 text-purple-700';
      case 'professional': return 'bg-indigo-100 text-indigo-700';
      case 'starter': return 'bg-blue-100 text-blue-700';
      case 'payg': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-zinc-100 text-zinc-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-zinc-900">User Management</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48"
          />
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Plans</option>
            <option value="free">Free</option>
            <option value="payg">PAYG</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="business">Business</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleMigrateAllUsers}
            disabled={migratingAll}
            className="text-amber-600 border-amber-300 hover:bg-amber-50"
          >
            {migratingAll ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
            Migrate All
          </Button>
          <Button variant="outline" size="sm" onClick={fetchUsers}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3">
          <CheckSquare className="h-4 w-4 text-indigo-600" />
          <span className="text-sm font-medium text-indigo-700">{selectedIds.length} user(s) selected</span>
          <span className="text-zinc-300">|</span>
          <span className="text-sm text-zinc-600">Change plan to:</span>
          <select
            value={bulkPlan}
            onChange={(e) => setBulkPlan(e.target.value)}
            className="border border-zinc-200 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="free">Free</option>
            <option value="payg">PAYG</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="business">Business</option>
          </select>
          <Button
            size="sm"
            onClick={handleBulkApply}
            disabled={bulkApplying}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {bulkApplying ? <RefreshCw className="h-3 w-3 mr-1.5 animate-spin" /> : null}
            Apply
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedIds([])}
            className="text-zinc-500"
          >
            Clear
          </Button>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                </th>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-right p-3">Wallet</th>
                <th className="text-right p-3">Usage</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Created</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr
                  key={user.id}
                  className={`border-b hover:bg-zinc-50 ${selectedIds.includes(user.id) ? 'bg-indigo-50' : ''}`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(user.id)}
                      onChange={() => toggleSelectUser(user.id)}
                      className="h-4 w-4 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                  </td>
                  <td className="p-3">
                    <div>
                      <p className="font-medium">{user.name || 'N/A'}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                      {user.is_test_user && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">TEST</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <select
                      value={user.plan || 'free'}
                      onChange={(e) => handleUpdatePlan(user.id, e.target.value)}
                      className={`text-xs border rounded px-2 py-1 ${getPlanBadgeClass(user.plan)}`}
                    >
                      <option value="free">Free</option>
                      <option value="payg">PAYG</option>
                      <option value="starter">Starter (₹799)</option>
                      <option value="professional">Professional (₹1,499)</option>
                      <option value="business">Business (₹4,999)</option>
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <div>
                      <p className="font-medium text-emerald-600">₹{(user.wallet_balance || 0).toLocaleString()}</p>
                      {user.credits_balance > 0 && (
                        <p className="text-xs text-zinc-400">{user.credits_balance?.toLocaleString() || 0} legacy</p>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <div>
                      <p className="font-medium">{(user.monthly_usage || 0).toLocaleString()}</p>
                      <p className="text-xs text-zinc-400">/ {(user.monthly_quota || 0).toLocaleString()}</p>
                    </div>
                  </td>
                  <td className="p-3">
                    {user.subscription_status === 'active' ? (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Active</span>
                    ) : user.plan === 'payg' ? (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">PAYG</span>
                    ) : (
                      <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-1 rounded">Free</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-zinc-500">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="outline" size="sm" onClick={() => setSelectedUser(user)}>
                        Details
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAddWalletBalance(user.id)}
                        title="Add Wallet Balance"
                        className="h-8 w-8 p-0"
                      >
                        <Wallet className="h-4 w-4 text-emerald-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleAddCredits(user.id)}
                        title="Add Credits (Legacy)"
                        className="h-8 w-8 p-0"
                      >
                        <DollarSign className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMigrateUser(user.id)}
                        title="Migrate to New Pricing"
                        className="h-8 w-8 p-0"
                      >
                        <Zap className="h-4 w-4 text-amber-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleImpersonate(user.id, user.email)}
                        title="Impersonate User"
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="User details" onClick={() => setSelectedUser(null)}>
          <Card className="w-full max-w-lg p-6 bg-white" onClick={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Customer Details</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedUser(null)}>Close</Button>
            </div>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              {[
                ['Full Name', selectedUser.name], ['Email', selectedUser.email], ['Phone', selectedUser.phone],
                ['Country', selectedUser.country], ['State', selectedUser.state], ['City', selectedUser.city],
                ['Address', selectedUser.address], ['Postal Code', selectedUser.postal_code],
                ['Plan', selectedUser.plan], ['Account Status', selectedUser.subscription_status || (selectedUser.plan === 'payg' ? 'PAYG' : 'Free')],
                ['Created Date', selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : 'N/A']
              ].map(([label, value]) => (
                <div key={label} className={label === 'Address' ? 'sm:col-span-2' : ''}>
                  <dt className="text-xs font-medium text-zinc-500">{label}</dt>
                  <dd className="break-words text-zinc-900">{value || '—'}</dd>
                </div>
              ))}
            </dl>
          </Card>
        </div>
      )}

      {filteredUsers.length === 0 && (
        <div className="text-center py-8 text-zinc-500">
          No users found matching "{search}"
        </div>
      )}
    </div>
  );
}

// Main Admin Dashboard
export default function AdminPage() {
  const navigate = useNavigate();
  const [adminToken, setAdminToken] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  // Lazy-mount: track which tabs have been visited — only render on first visit
  const [mountedTabs, setMountedTabs] = useState(new Set(['overview']));
  
  // WebSocket for real-time updates
  const {
    isConnected,
    events,
    connectionCount,
    onlineUsers,
    clearEvents,
    reconnect
  } = useAdminWebSocket(adminToken);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      await axios.get(`${API}/api/admin/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminToken(token);
      fetchOverview(token);
    } catch (error) {
      localStorage.removeItem('admin_token');
      setLoading(false);
    }
  };

  const fetchOverview = async (token) => {
    try {
      const response = await axios.get(`${API}/api/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setAdminToken(null);
    toast.success('Logged out');
  };

  const handleLogin = (token) => {
    setAdminToken(token);
    fetchOverview(token);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (!adminToken) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-zinc-100">
      {/* Header */}
      <header className="bg-zinc-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-indigo-400" />
              <span className="text-xl font-bold">Admin Panel</span>
              {/* WebSocket Status Indicator */}
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${
                isConnected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
              }`}>
                <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                {isConnected ? 'Live' : 'Offline'}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-zinc-300 hover:text-white"
              >
                Back to App
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-zinc-300 hover:text-white"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
              <Users className="h-6 w-6 mb-2 opacity-80" />
              <p className="text-sm opacity-80">Total Users</p>
              <p className="text-3xl font-bold">{overview.total_users}</p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
              <BarChart3 className="h-6 w-6 mb-2 opacity-80" />
              <p className="text-sm opacity-80">Translations Today</p>
              <p className="text-3xl font-bold">{overview.translations_today}</p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
              <DollarSign className="h-6 w-6 mb-2 opacity-80" />
              <p className="text-sm opacity-80">Revenue (Month)</p>
              <p className="text-3xl font-bold">₹{overview.revenue_this_month?.toLocaleString() || 0}</p>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <Cpu className="h-6 w-6 mb-2 opacity-80" />
              <p className="text-sm opacity-80">Words Translated</p>
              <p className="text-3xl font-bold">
                {(overview.total_words_translated / 1000).toFixed(1)}K
              </p>
            </Card>
          </div>
        )}

        {/* Full-width tab navigation — outside grid so all 9 tabs are always visible */}
        <Tabs value={activeTab} onValueChange={(v) => {
          setActiveTab(v);
          setMountedTabs(prev => { const s = new Set(prev); s.add(v); return s; });
        }}>
          <div className="overflow-x-auto mb-6">
            <TabsList className="bg-white shadow-sm inline-flex h-auto gap-1 p-1 whitespace-nowrap">
              <TabsTrigger value="overview" className="gap-2">
                <BarChart3 className="h-4 w-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="users" className="gap-2">
                <Users className="h-4 w-4" />
                Users
              </TabsTrigger>
              <TabsTrigger value="revenue" className="gap-2">
                <TrendingUp className="h-4 w-4" />
                Revenue
              </TabsTrigger>
              <TabsTrigger value="audit" className="gap-2">
                <Shield className="h-4 w-4" />
                Audit Log
              </TabsTrigger>
              <TabsTrigger value="system" className="gap-2">
                <Server className="h-4 w-4" />
                System
              </TabsTrigger>
              <TabsTrigger value="feedback" className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Feedback
              </TabsTrigger>
              <TabsTrigger value="export_feedback" className="gap-2">
                <Star className="h-4 w-4" />
                Export Ratings
              </TabsTrigger>
              <TabsTrigger value="costs" className="gap-2">
                <DollarSign className="h-4 w-4" />
                Service Costs
              </TabsTrigger>
              <TabsTrigger value="errors" className="gap-2">
                <AlertTriangle className="h-4 w-4" />
                Errors
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Content + Live Activity side by side */}
          <div className="grid lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 min-w-0">
              <TabsContent value="overview">
                <div className="space-y-6">
                  <OnlineUsersBox onlineUsers={onlineUsers} />
                  <UserAnalytics adminToken={adminToken} />
                </div>
              </TabsContent>
              <TabsContent value="users">
                {mountedTabs.has('users') && <UserManagement adminToken={adminToken} />}
              </TabsContent>
              <TabsContent value="revenue">
                {mountedTabs.has('revenue') && <RevenueAnalytics adminToken={adminToken} />}
              </TabsContent>
              <TabsContent value="audit">
                {mountedTabs.has('audit') && <AuditLog adminToken={adminToken} />}
              </TabsContent>
              <TabsContent value="system">
                {mountedTabs.has('system') && <SystemHealth adminToken={adminToken} />}
              </TabsContent>
              <TabsContent value="feedback">
                {mountedTabs.has('feedback') && <FeedbackManagement adminToken={adminToken} />}
              </TabsContent>
              <TabsContent value="export_feedback">
                {mountedTabs.has('export_feedback') && <ExportFeedbackAdmin adminToken={adminToken} />}
              </TabsContent>
              <TabsContent value="costs">
                {mountedTabs.has('costs') && <CostDashboard adminToken={adminToken} />}
              </TabsContent>
              <TabsContent value="errors">
                {mountedTabs.has('errors') && <ErrorLog adminToken={adminToken} />}
              </TabsContent>
            </div>

            {/* Live Activity Feed — always visible alongside any tab */}
            <div className="lg:col-span-1 lg:sticky lg:top-6 h-[calc(100vh-280px)] min-h-[400px]">
              <LiveActivityFeed
                events={events}
                isConnected={isConnected}
                onClear={clearEvents}
                onReconnect={reconnect}
                connectionCount={connectionCount}
              />
            </div>
          </div>
        </Tabs>
      </main>
    </div>
  );
}
